<script setup lang="ts">
/**
 * Intent Capture Modal
 *
 * A modal dialog that captures user intent from mega menu "I need to..." links.
 * Wraps the QuickEnquiryForm with context-aware title and service information.
 */

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import QuickEnquiryForm from '@/components/forms/QuickEnquiryForm.vue'

const { isOpen, config, close } = useIntentModal()

function handleSuccess() {
  // Keep modal open briefly to show success state, then close
  setTimeout(() => {
    close()
  }, 2000)
}

/**
 * Reka-ui auto-focuses the first focusable descendant on open, which triggers the
 * browser's default scroll-into-view and causes the page to jerk upward on mobile.
 * Refocus the dialog container itself with { preventScroll: true } — keyboard users
 * still land inside the dialog and Tab moves to the first field normally.
 */
function handleOpenAutoFocus(event: Event) {
  event.preventDefault()
  const target = event.currentTarget as HTMLElement | null
  target?.focus({ preventScroll: true })
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-w-md p-0 gap-0 overflow-hidden"
      @open-auto-focus="handleOpenAutoFocus"
    >
      <!-- Header with context -->
      <div class="border-b border-neutral-200 bg-neutral-50 px-6 py-4 pr-12">
        <DialogTitle class="text-lg font-bold text-slate-900">
          {{ config?.title ?? 'Get in Touch' }}
        </DialogTitle>
        <DialogDescription v-if="config?.description" class="text-sm text-neutral-600 mt-1">
          {{ config.description }}
        </DialogDescription>
      </div>

      <!-- Form with its own padding via variant -->
      <QuickEnquiryForm
        :service="config?.service ?? ''"
        :title="''"
        variant="modal"
        submit-text="Send Enquiry"
        :show-company="!config?.hideCompany"
        @success="handleSuccess"
      />
    </DialogContent>
  </Dialog>
</template>
